import React from 'react';
import { AlertTriangle, X, ShieldCheck } from 'lucide-react';
import { SubscriptionTier } from '@everyday-os/shared';

interface FeatureLoss {
  name: string;
  lostAt: SubscriptionTier; // tier at which access is lost
}

const FEATURE_LOSSES: FeatureLoss[] = [
  { name: 'Contacts management',    lostAt: SubscriptionTier.FREE },
  { name: 'Maintenance tracking',   lostAt: SubscriptionTier.FREE },
  { name: 'Pets & Vehicles',         lostAt: SubscriptionTier.FREE },
  { name: 'Bills & Finance',         lostAt: SubscriptionTier.FREE },
  { name: 'Meal Planner',            lostAt: SubscriptionTier.BASIC },
  { name: 'Document Vault',          lostAt: SubscriptionTier.BASIC },
  { name: 'AI Assistant',            lostAt: SubscriptionTier.BASIC },
];

const TIER_ORDER: Record<string, number> = { FREE: 0, BASIC: 1, PREMIUM: 2 };

interface Props {
  currentTier: SubscriptionTier;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function DowngradeWarningModal({ currentTier, onConfirm, onCancel, isLoading }: Props) {
  // Show features the user will lose if they fully cancel (drop to FREE)
  const lostFeatures = FEATURE_LOSSES.filter(f =>
    TIER_ORDER[f.lostAt.toUpperCase()] >= TIER_ORDER[currentTier.toUpperCase()]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} aria-hidden="true" />

      <div
        className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: 'var(--theme-card)', border: '1px solid var(--theme-border)' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: 'var(--theme-text)' }}>
                Manage Your Subscription
              </h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-text-muted)' }}>
                Review what changes when you cancel or downgrade
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="w-4 h-4" style={{ color: 'var(--theme-text-muted)' }} />
          </button>
        </div>

        {/* Feature loss list */}
        {lostFeatures.length > 0 && (
          <div className="px-6 pb-4">
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--theme-text-muted)' }}>
              Features you'll lose access to
            </p>
            <ul className="space-y-1.5">
              {lostFeatures.map(f => (
                <li key={f.name} className="flex items-center gap-2 text-sm" style={{ color: 'var(--theme-text)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  {f.name}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Data safety note */}
        <div className="mx-6 mb-5 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-700 dark:text-emerald-300">
            <span className="font-semibold">Your data is never deleted.</span> Everything stays safely stored
            and becomes accessible again if you re-subscribe.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onCancel}
            className="flex-1 h-11 rounded-xl border text-sm font-semibold transition-colors"
            style={{ borderColor: 'var(--theme-border)', color: 'var(--theme-text)' }}
          >
            Stay on Current Plan
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Opening Portal...' : 'Continue to Billing Portal'}
          </button>
        </div>
      </div>
    </div>
  );
}
